/**
 * Les mentions de TVA d'un devis ou d'une facture, selon la date du document.
 *
 * LA TVA CHANGE DE CODE LE 1er JANVIER 2027. L'ordonnance n° 2025-1247 du
 * 17 décembre 2025 transfère ses règles du code général des impôts (CGI) vers
 * le code des impositions sur les biens et services (CIBS). L'ordonnance
 * n° 2026-671 du 27 juillet 2026 a reporté ce transfert du 1er septembre 2026
 * au 1er janvier 2027 (article 17, 2°) et renuméroté le livre II : la
 * définition de l'entreprise franchisée, premier alinéa du I de l'article
 * 293 B du CGI, devient l'article L. 233-3 du CIBS. Relu le 10 septembre 2026
 * sur Légifrance, dans l'annexe de l'ordonnance et dans la table de
 * concordance du JO n° 0174 du 28 juillet 2026.
 *
 * LA MENTION SUIT LA DATE DU DOCUMENT, jamais celle du jour où on le relit :
 * un devis daté du 15 décembre 2026 porte 293 B, celui du 4 janvier 2027 porte
 * L. 233-3.
 *
 * DEUX PIÈGES, que le harnais refuse l'un et l'autre :
 *   - « L. 223-3 », que cite encore une page d'impots.gouv.fr modifiée le
 *     16 juin 2026, est la numérotation d'AVANT la renumérotation. À partir du
 *     1er janvier 2027, ce numéro désigne un autre article, dans le chapitre
 *     des déductions : le recopier renverrait le lecteur au mauvais texte.
 *   - Les renvois au CGI restent admis sur les factures jusqu'au 30 juin 2028
 *     (article 46, II de l'ordonnance de 2025, modifié par l'article 17, 1°, de
 *     celle de 2026). Une facture de 2027 qui porte encore 293 B n'est donc pas
 *     irrégulière. Le document composé ici porte quand même la référence en
 *     vigueur à sa date.
 *
 * L'AUTOLIQUIDATION NE CHANGE PAS : la mention retenue, « Autoliquidation », ne
 * cite aucun article. Seule la variante qui renvoie à l'article 283-2 du CGI
 * s'éteint avec la tolérance, et aucune des deux tables de concordance ne lui
 * donne d'article de remplacement : le module n'en invente pas.
 *
 *   npm run check:vat-mentions
 */

/** Le premier jour où la TVA se lit dans le CIBS. */
export const BASCULE_CIBS = "2027-01-01";

/** Le dernier jour où un renvoi au CGI reste admis sur une facture. */
export const FIN_RENVOIS_CGI = "2028-06-30";

export const MENTION_FRANCHISE_CGI = "TVA non applicable, article 293 B du CGI";
export const MENTION_FRANCHISE_CIBS = "TVA non applicable, article L. 233-3 du CIBS";

/** Les deux autres formulations que le BOFiP admet (BOI-TVA-DECLA-40-10-20). */
const MENTION_FRANCHISE_EUROPEENNE_CGI = "TVA non applicable, article 293 B bis du CGI";
const MENTION_FRANCHISE_DIRECTIVE = "TVA non applicable, article 284 de la directive 2006/112/CE";

export const MENTION_AUTOLIQUIDATION = "Autoliquidation";
const AUTOLIQUIDATION_DIRECTIVE = "TVA due par le preneur, article 196 de la directive 2006/112/CE";
const AUTOLIQUIDATION_CGI = "TVA due par le preneur, article 283-2 du code général des impôts";

/**
 * Le jour d'une date, au format AAAA-MM-JJ, ou null.
 *
 * Une date ISO complète garde ses dix premiers caractères, c'est-à-dire le
 * jour UTC. Une page rendue entre minuit et une heure du matin à Paris, la nuit
 * du Nouvel An, dit donc encore 2026 : les deux mentions étant admises ce
 * jour-là, l'écart ne coûte rien, et il ne touche jamais un devis, que son
 * auteur date lui-même.
 */
export function jourDu(date) {
    const j = String(date ?? "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(j)) return null;
    const d = new Date(`${j}T00:00:00Z`);
    if (Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== j) return null;
    return j;
}

/**
 * La mention de franchise en base à porter sur un document daté de `date`.
 *
 * Rend null sans date lisible : choisir une mention au hasard serait pire que
 * de ne rien écrire, et un devis refuse déjà de se composer sans date.
 */
export function mentionFranchise(date) {
    const j = jourDu(date);
    if (!j) return null;
    return j < BASCULE_CIBS ? MENTION_FRANCHISE_CGI : MENTION_FRANCHISE_CIBS;
}

/**
 * Toutes les formulations admises à cette date, la retenue en premier.
 *
 * Le BOFiP en admet trois, et le référentiel interdit d'en présenter une comme
 * la seule. Du 1er janvier 2027 au 30 juin 2028, les renvois au CGI restent
 * admis à côté du CIBS ; ensuite, il ne reste que le CIBS et la directive.
 * L'équivalent de 293 B bis dans le CIBS n'est pas donné : la table de
 * concordance le répartit sur cinq articles, et aucun n'est arbitré.
 */
export function mentionsFranchiseAdmises(date) {
    const j = jourDu(date);
    if (!j) return [];
    if (j < BASCULE_CIBS) {
        return [MENTION_FRANCHISE_CGI, MENTION_FRANCHISE_EUROPEENNE_CGI, MENTION_FRANCHISE_DIRECTIVE];
    }
    if (j <= FIN_RENVOIS_CGI) {
        return [
            MENTION_FRANCHISE_CIBS,
            MENTION_FRANCHISE_CGI,
            MENTION_FRANCHISE_EUROPEENNE_CGI,
            MENTION_FRANCHISE_DIRECTIVE,
        ];
    }
    return [MENTION_FRANCHISE_CIBS, MENTION_FRANCHISE_DIRECTIVE];
}

/** Les formulations d'autoliquidation admises à cette date, la retenue en premier. */
export function mentionsAutoliquidationAdmises(date) {
    const j = jourDu(date);
    if (!j) return [];
    return j <= FIN_RENVOIS_CGI
        ? [MENTION_AUTOLIQUIDATION, AUTOLIQUIDATION_DIRECTIVE, AUTOLIQUIDATION_CGI]
        : [MENTION_AUTOLIQUIDATION, AUTOLIQUIDATION_DIRECTIVE];
}
